import { useEffect, useRef, useState } from 'react'
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import { Icon } from './Icon'

export interface ThankYouTemplateView {
  id: string
  title: string
  previewText: string
}

export type ThankYouRecipientStatus = 'sent' | 'no_email' | 'failed'

export interface ThankYouRecipientResult {
  userId: string
  userName: string
  status: ThankYouRecipientStatus
}

export interface ThankYouSendResult {
  emailSent: number
  total: number
  results: ThankYouRecipientResult[]
}

export interface ThankYouCardProps {
  /** ISO timestamp of the last send, or null if never. */
  sentAt: string | null
  participantNames: string[]
  templates: ThankYouTemplateView[]
  /**
   * Send it. Exactly one of templateId / customBody; `confirm` is set by the card
   * when re-sending after one already went out. Resolves with the honest result, or
   * rejects (the card shows the error).
   */
  onSend: (req: { templateId?: string; customBody?: string; confirm?: boolean }) => Promise<ThankYouSendResult>
}

type Stage = 'picker' | 'preview' | 'edit' | 'confirm' | 'sending' | 'results'

/** Where the Header's back arrow returns to, per stage. */
const BACK_TO: Partial<Record<Stage, Stage>> = { preview: 'picker', edit: 'preview', confirm: 'preview' }

/** The character cap mirrored from the API's SendThankYouValidator. */
const MAX_BODY = 400

/**
 * The "Thank the participants" card on an ended hunt, and the modal behind it:
 * pick a built-in template (server-composed preview) → see exactly how it reads →
 * optionally edit → send → an honest per-recipient result with retry.
 *
 * Presentation only: the data and the send come in as props, so this holds no API
 * knowledge and the domain wires the hooks.
 */
export function ThankYouCard({ sentAt, participantNames, templates, onSend }: ThankYouCardProps) {
  const { t, i18n } = useTranslation('huntInvitation')
  const [open, setOpen] = useState(false)
  const [stage, setStage] = useState<Stage>('picker')
  const [picked, setPicked] = useState<ThankYouTemplateView | null>(null)
  const [edited, setEdited] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [result, setResult] = useState<ThankYouSendResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  const count = participantNames.length
  const recipientLine = t('thankYou.recipients', { count, names: participantNames.join(', ') })
  const bodyText = edited ?? picked?.previewText ?? ''

  const start = () => {
    setStage('picker'); setPicked(null); setEdited(null); setError(null); setResult(null); setOpen(true)
  }
  // A send in flight must not be torn down by a stray backdrop click or Escape —
  // the request already went to the server, and closing would lose the result.
  const close = () => { if (stage !== 'sending') setOpen(false) }

  // Move focus into the dialog when it opens, and let Escape close it — neither is
  // free with a hand-rolled modal, and a keyboard user would otherwise be stranded.
  useEffect(() => {
    if (!open) return
    dialogRef.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stage])

  const send = async () => {
    if (!picked) return
    setStage('sending'); setError(null)
    try {
      const res = await onSend({
        templateId: edited === null ? picked.id : undefined,
        customBody: edited ?? undefined,
        confirm: sentAt != null,
      })
      setResult(res); setStage('results')
    } catch (e) {
      // The domain container translates a known code (already-sent, transport
      // unavailable) into a localized message before it reaches here; anything else
      // falls back to the generic error. Never surface a raw "HTTP 409".
      setError(e instanceof Error && e.message ? e.message : t('thankYou.error'))
      setStage('preview')
    }
  }

  return (
    <div className="wh-card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface }}>{t('thankYou.cardTitle')}</div>
        <div style={{ fontSize: 12, color: COLORS.outline }}>
          {sentAt
            ? t('thankYou.sentAt', { date: new Date(sentAt).toLocaleDateString(i18n.language) })
            : t('thankYou.notSent')}
        </div>
      </div>
      <button className="wh-btn wh-btn-outline" onClick={start} disabled={count === 0}>
        {sentAt ? t('thankYou.sendAgain') : t('thankYou.send')}
      </button>

      {open && (
        <div
          role="dialog" aria-modal="true" aria-label={t('thankYou.modalTitle')}
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={close}
        >
          <div
            ref={dialogRef}
            tabIndex={-1}
            className="wh-card"
            style={{ width: '100%', maxWidth: 480, background: COLORS.surfaceContainerLowest, borderRadius: 12, overflow: 'hidden', maxHeight: 'calc(100% - 32px)', display: 'flex', flexDirection: 'column', outline: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Header
              title={stage === 'picker' ? t('thankYou.modalTitle') : (picked?.title ?? t('thankYou.modalTitle'))}
              subtitle={recipientLine}
              onBack={BACK_TO[stage] ? () => setStage(BACK_TO[stage]!) : undefined}
              onClose={close}
              backLabel={t('thankYou.back')}
              closeLabel={t('thankYou.close')}
            />

            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {stage === 'picker' && (
                <>
                  <div style={{ ...label, marginBottom: 4 }}>{t('thankYou.templatesHeading')}</div>
                  {templates.map((tpl, i) => (
                    <button
                      key={tpl.id}
                      onClick={() => { setPicked(tpl); setEdited(null); setStage('preview') }}
                      style={{ ...row, borderTop: i > 0 ? `1px solid ${COLORS.surfaceContainerHighest}` : 'none' }}
                    >
                      {/* minWidth:0 lets this flex child shrink so the preview's
                          ellipsis engages instead of overflowing the modal. */}
                      <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.title}</div>
                        <div style={{ fontSize: 12, color: COLORS.outline, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.previewText}</div>
                      </div>
                      <Icon name="chevron-right" size={16} />
                    </button>
                  ))}
                  <div style={{ marginTop: 12, fontSize: 11, color: COLORS.outline, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <Icon name="info" size={13} />{t('thankYou.languageNote')}
                  </div>
                </>
              )}

              {stage === 'preview' && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={label}>{t('thankYou.howItReads')}</div>
                    <div style={{ flex: 1 }} />
                    <button className="wh-btn wh-btn-text" style={{ fontSize: 12 }} onClick={() => { setDraft(bodyText); setStage('edit') }}>{t('thankYou.edit')}</button>
                  </div>
                  <div style={{ whiteSpace: 'pre-line', fontSize: 13, color: COLORS.onSurface, background: COLORS.surfaceContainerLow, borderRadius: 8, padding: 12, marginTop: 4 }}>{bodyText}</div>
                  <div style={{ fontSize: 11, color: COLORS.outline, margin: '6px 0 12px' }}>
                    {edited === null ? t('thankYou.sentAsTemplate') : t('thankYou.sentAsWritten')}
                  </div>
                  <Channel icon="user" text={recipientLine} />
                  <Channel icon="mail" text={t('thankYou.byEmail')} />
                  {error && <div role="alert" style={{ marginTop: 10, color: COLORS.error, fontSize: 12 }}>{error}</div>}
                </>
              )}

              {stage === 'edit' && (
                <>
                  <div style={label}>{t('thankYou.yourWording')}</div>
                  <textarea
                    value={draft} maxLength={MAX_BODY} rows={5} autoFocus
                    onChange={(e) => setDraft(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', marginTop: 4, padding: 10, borderRadius: 8, border: `1.5px solid ${COLORS.outlineVariant}`, fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
                  />
                  <div style={{ display: 'flex', fontSize: 11, color: COLORS.outline, marginTop: 4 }}>
                    <span>{t('thankYou.templateUnchanged')}</span><span style={{ flex: 1 }} /><span>{draft.length} / {MAX_BODY}</span>
                  </div>
                </>
              )}

              {stage === 'confirm' && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '4px 0' }}>
                  <Icon name="info" size={18} color={COLORS.error} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface }}>{t('thankYou.resendConfirmTitle')}</div>
                    <div style={{ fontSize: 13, color: COLORS.onSurfaceVariant, marginTop: 4 }}>
                      {sentAt
                        ? t('thankYou.resendConfirmBody', { date: new Date(sentAt).toLocaleDateString(i18n.language) })
                        : t('thankYou.resendConfirmBodyNoDate')}
                    </div>
                  </div>
                </div>
              )}

              {stage === 'sending' && (
                <div style={{ textAlign: 'center', padding: 24, color: COLORS.outline }}>{t('thankYou.sending')}</div>
              )}

              {stage === 'results' && result && (
                <>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface, marginBottom: 8 }}>
                    {t('thankYou.resultSummary', { sent: result.emailSent, total: result.total })}
                  </div>
                  {result.results.map((r) => (
                    <div key={r.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13 }}>
                      <Icon name={r.status === 'sent' ? 'check' : 'info'} size={14} color={r.status === 'sent' ? COLORS.primary : COLORS.error} />
                      <span style={{ flex: 1, color: COLORS.onSurface }}>{r.userName}</span>
                      <span style={{ fontSize: 11, color: COLORS.outline }}>{t(`thankYou.status.${r.status}`)}</span>
                    </div>
                  ))}
                </>
              )}
            </div>

            <Footer>
              {stage === 'preview' && (
                <>
                  <div style={{ flex: 1 }} />
                  <button className="wh-btn wh-btn-ghost" onClick={close}>{t('thankYou.cancel')}</button>
                  {/* A first send goes straight out; a re-send asks first, so nobody
                      re-thanks the whole team by a reflex click. */}
                  <button className="wh-btn wh-btn-primary" onClick={sentAt ? () => setStage('confirm') : send}>
                    <Icon name="send" size={16} />{t('thankYou.sendNow')}
                  </button>
                </>
              )}
              {stage === 'confirm' && (
                <>
                  <div style={{ flex: 1 }} />
                  <button className="wh-btn wh-btn-ghost" onClick={() => setStage('preview')}>{t('thankYou.cancel')}</button>
                  <button className="wh-btn wh-btn-primary" onClick={send}>
                    <Icon name="send" size={16} />{t('thankYou.resendConfirmAction')}
                  </button>
                </>
              )}
              {stage === 'edit' && (
                <>
                  <div style={{ flex: 1 }} />
                  <button className="wh-btn wh-btn-ghost" onClick={() => setStage('preview')}>{t('thankYou.cancel')}</button>
                  <button className="wh-btn wh-btn-primary" onClick={() => { setEdited(draft.trim()); setStage('preview') }} disabled={draft.trim().length === 0}>{t('thankYou.done')}</button>
                </>
              )}
              {(stage === 'picker' || stage === 'results') && (
                <>
                  <div style={{ flex: 1 }} />
                  <button className="wh-btn wh-btn-primary" onClick={close}>{stage === 'results' ? t('thankYou.close') : t('thankYou.cancel')}</button>
                </>
              )}
            </Footer>
          </div>
        </div>
      )}
    </div>
  )
}

const label: React.CSSProperties = { fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: COLORS.outline }
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer' }

function Channel({ icon, text }: { icon: string; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, color: COLORS.onSurfaceVariant }}>
      <Icon name={icon} size={15} />{text}
    </div>
  )
}

function Header(
  { title, subtitle, onBack, onClose, backLabel, closeLabel }:
  { title: string; subtitle: string; onBack?: () => void; onClose: () => void; backLabel: string; closeLabel: string },
) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 12px', borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`, flexShrink: 0 }}>
      {onBack && <button className="wh-btn-icon" onClick={onBack} aria-label={backLabel}><Icon name="chevron-left" size={18} /></button>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.onSurface }}>{title}</div>
        <div style={{ fontSize: 12, color: COLORS.outline, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>
      </div>
      <button className="wh-btn-icon" onClick={onClose} aria-label={closeLabel}><Icon name="x" size={18} /></button>
    </div>
  )
}

function Footer({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, flexShrink: 0 }}>
      {children}
    </div>
  )
}
